import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { PermissionsData } from '@core/permissions/actions';

/**
 * Permission store — ports `core/services/permission.service.ts`.
 *
 * Three fixes over the Angular version:
 *
 * 1. PERSISTED. The Angular service held permissions in two in-memory
 *    BehaviorSubjects, so every page refresh rendered with no permissions until
 *    `GET /users/api/permissions` resolved — nav items and action buttons visibly
 *    flashed in. Persisting to localStorage means the UI is correct on first paint
 *    and the network refresh happens behind it.
 *
 * 2. LOADED ONCE. `loadPermissions()` was called from BOTH `AppComponent.ngOnInit`
 *    and `HeaderComponent.ngOnInit`, firing two identical requests on every boot.
 *    The query now lives in one place (`MainLayout`) with `staleTime: Infinity`.
 *
 * 3. Cleared on logout, so a second user on a shared machine never inherits the
 *    first user's permissions from persisted state.
 *
 * `hasPermission` is read SYNCHRONOUSLY from ~20 imperative event handlers, which is
 * why this is a Zustand store (`getState()`) and not a query hook.
 */
interface PermissionState {
  actions: Record<string, boolean> | null;
  groups: Record<string, { visible?: boolean; actions?: string[] }> | null;
  /**
   * Somebody has delegated their approval authority to this user.
   *
   * ⚠️ NOT A PERMISSION, and kept out of `actions` for that reason. It is a fact about a live
   * delegation, so it can flip between two loads with no change to the user's role — and it is
   * persisted with the rest, so a refresh does not silently drop the extra reach until the next fetch.
   */
  isDelegatee: boolean;
  /** Epoch ms of the last successful load; `null` means never loaded. */
  loadedAt: number | null;
  setPermissions: (data: PermissionsData) => void;
  clear: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  persist(
    (set) => ({
      actions: null,
      groups: null,
      isDelegatee: false,
      loadedAt: null,

      setPermissions: ({ actions, groups, isDelegatee }) =>
        set({
          actions: actions ?? null,
          groups: groups ?? null,
          isDelegatee: isDelegatee === true,
          loadedAt: Date.now(),
        }),

      // `isDelegatee` back to false on logout, like the rest — a delegation granted to one user must
      // not survive into the next session on a shared machine.
      clear: () => set({ actions: null, groups: null, isDelegatee: false, loadedAt: null }),
    }),
    {
      name: 'vm.permissions',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
